<template>
	<v-dialog v-model="dialog" fullscreen hide-overlay transition="dialog-bottom-transition" scrollable>
		<v-card tile>
			<v-toolbar card dark color="primary">
				<v-btn icon dark @click.native="closeEditor(false)" :disabled="isSaving">
					<v-icon>close</v-icon>
				</v-btn>
				<v-toolbar-title>{{ title }}</v-toolbar-title>
				<v-spacer></v-spacer>
				<v-toolbar-items>
					<v-btn :loading="isSaving" :disabled="isSaving" dark flat @click="onSaveClick">Save <span slot="loader">Saving...</span></v-btn>
				</v-toolbar-items>
			</v-toolbar>
			<v-card-text>
				<div class="text-xs-left mt-2" style="padding-left: 24px">
					<v-btn v-if="isNew" color="info" @click="positionTemplateSelectorDialog = !positionTemplateSelectorDialog">Load from template</v-btn>
					<div v-else-if="isAdmin"><a href="#" @click="convertToTemplateClick">Convert this {{ $term('positionDescription', { case: 'title' }) }} into a Template</a></div>
				</div>

				<v-container grid-list-xl fluid>
					<v-layout row wrap>
						<v-flex lg5>
							<v-card class="mb-4">
								<v-toolbar flat dense cad>
									<v-toolbar-title class="subheading">Position Detail</v-toolbar-title>
									<v-spacer></v-spacer>
								</v-toolbar>

								<v-divider></v-divider>

								<v-card-text class>
									<v-text-field v-model="form.title" :error-messages="titleErrors" label="Job Title" required @input="$v.form.title.$touch()" @blur="$v.form.title.$touch()"></v-text-field>

									<v-select v-model="form.type" :items="positionTypes" :error-messages="typeErrors" item-text="text" label="Position Type" required @change="$v.form.type.$touch()" @blur="$v.form.type.$touch()"></v-select>

									<v-select
										v-model="form.reportsTo"
										:items="reportsToEmployees"
										:error-messages="reportsToErrors"
										item-text="fullName"
										return-object
										label="Reports To"
										required
										@change="$v.form.reportsTo.$touch()"
										@blur="$v.form.reportsTo.$touch()"
									></v-select>

									<v-menu
										ref="startDateMenu"
										v-model="form.startDateUTC.menu"
										:close-on-content-click="false"
										:nudge-right="40"
										:return-value.sync="form.startDateUTC.date"
										lazy
										transition="scale-transition"
										offset-y
										full-width
										min-width="290px"
									>
										<v-text-field slot="activator" v-model="effectiveDateFormatted" label="Effective Date" readonly :error-messages="startDateUTCErrors"></v-text-field>
										<v-date-picker v-model="form.startDateUTC.date" @input="$refs.startDateMenu.save(form.startDateUTC.date)"></v-date-picker>
									</v-menu>

									<v-textarea v-model="form.description" :error-messages="descriptionErrors" required box label="Position Purpose" @input="$v.form.description.$touch()" @blur="$v.form.description.$touch()"></v-textarea>
								</v-card-text>
							</v-card>
						</v-flex>

						<v-flex lg7 xs12>
							<v-tabs v-model="active_tab" left fixed-tabs show-arrows color="info" dark slider-color="white">
								<v-tab>KPI's</v-tab>
								<v-tab class="noPrint">Key Duties</v-tab>
								<v-tab class="noPrint">Person Specification</v-tab>

								<v-tab-item>
									<v-card class="mb-4" flat>
										<v-divider></v-divider>

										<v-card-text>
											<kpi-helper />

											<employee-position-item-list
												item-header-text="KPI"
												:position-items="kpis"
												enableReorder
												@onSortOrderUpdated="onSortOrderUpdated"
												@onEditPositionItem="onEditPositionItem"
												@onDeletePositionItem="onDeletePositionItem"
											>
											</employee-position-item-list>
										</v-card-text>
										<v-card-actions>
											<v-spacer></v-spacer>
											<v-btn color="primary" small outline @click="onAddPositionItem(1)"> <v-icon left>add</v-icon>Add New KPI</v-btn>
										</v-card-actions>
									</v-card>
								</v-tab-item>

								<v-tab-item>
									<v-card class="mb-4" flat>
										<v-divider></v-divider>

										<v-card-text>
											<key-duty-helper />

											<employee-position-item-list
												item-header-text="Key Duty"
												:position-items="keyDuties"
												enableReorder
												@onSortOrderUpdated="onSortOrderUpdated"
												@onEditPositionItem="onEditPositionItem"
												@onDeletePositionItem="onDeletePositionItem"
											>
											</employee-position-item-list>
										</v-card-text>
										<v-card-actions>
											<v-spacer></v-spacer>
											<v-btn color="primary" small outline @click="onAddPositionItem(2)"> <v-icon left>add</v-icon>Add New Key Duty</v-btn>
										</v-card-actions>
									</v-card>
								</v-tab-item>
								<v-tab-item>
									<v-card class="mb-4" flat>
										<v-divider></v-divider>

										<v-card-text>
											<person-specification-helper />

											<employee-position-item-list
												:show-category-column="false"
												item-header-text="Specification"
												:position-items="personSpecification"
												enableReorder
												@onSortOrderUpdated="onSortOrderUpdated"
												@onEditPositionItem="onEditPositionItem"
												@onDeletePositionItem="onDeletePositionItem"
											></employee-position-item-list>
										</v-card-text>
										<v-card-actions>
											<v-spacer></v-spacer>
											<v-btn color="primary" small outline @click="onAddPositionItem(5)"> <v-icon left>add</v-icon>Add New Specification</v-btn>
										</v-card-actions>
									</v-card>
								</v-tab-item>
							</v-tabs>

							<employee-position-item-editor
								v-if="positionItemDialog"
								:position-item="editedPositionItem"
								:performance-review="false"
								:type="positionItemDialogType"
								:hide-category="positionItemDialogType == 5 ? true : false"
								@save="savePositionItem"
								@closeEditor="closePositionItemEditor"
							></employee-position-item-editor>

							<add-position-item-multi v-if="addPositionItemDialog" :performance-review="false" :type="positionItemDialogType" @save="savePositionItemAddMulti" @closeEditor="closePositionItemEditor"></add-position-item-multi>

							<position-template-selector
								:position-templates="positionTemplates"
								:dialog="positionTemplateSelectorDialog"
								@onSelectPositionTemplate="onSelectPositionTemplate"
								@onCloseSelector="closePositionTemplateSelector"
							></position-template-selector>
						</v-flex>
					</v-layout>
				</v-container>
			</v-card-text>

			<loading-modal v-if="isLoadingPositionTemplate" loading-text="Loading Template..." />

			<div style="flex: 1 1 auto"></div>
		</v-card>
		<update-position-confirmation-dialog 
		:dialog="showConfirmDialog" 
		:currentStatus="position.status"
		:coreItemsUpdated="coreItemsUpdated"
		:requireEmployeeAcknowledgement="requireEmployeeAcknowledgement"
		@onConfirm="submit" 
		@onCancel="showConfirmDialog = false" />
	</v-dialog>
</template>

<script>
	import { validationMixin } from 'vuelidate';
	import typeFriendlyMixin from '@/mixins/typeFriendlyMixin';
	import { required } from 'vuelidate/lib/validators';
	import EmployeePositionItemList from '@/components/employee/EmployeePositionItemList';
	import EmployeePositionItemEditor from '@/components/employee/EmployeePositionItemEditor';
	import PositionTemplateSelector from '@/components/positionTemplate/PositionTemplateSelector';
	import AddPositionItemMulti from '@/components/AddPositionItemMulti';
	import KpiHelper from '@/components/helpers/KpiHelper';
	import PersonSpecificationHelper from '@/components/helpers/PersonSpecificationHelper';
	import KeyDutyHelper from '@/components/helpers/KeyDutyHelper';
	import LoadingModal from '@/components/LoadingModal';
	import UpdatePositionConfirmationDialog from '@/components/employee/UpdatePositionConfirmationDialog';

	export default {
		components: {
			EmployeePositionItemEditor,
			EmployeePositionItemList,
			PositionTemplateSelector,
			AddPositionItemMulti,
			KpiHelper,
			PersonSpecificationHelper,
			KeyDutyHelper,
			LoadingModal,
			UpdatePositionConfirmationDialog
		},
		props: {
			employees: {
				required: true
			},
			positionTemplates: {
				required: true
			},
			employeeId: {
				type: String
			},
			position: {
				type: Object,
				default: () => ({
					title: '',
					description: '',
					reportsTo: null,
					startDateUTC: null,
					endDateLocal: null,
					positionItems: [],
					type: 0
				})
			},
			requireEmployeeAcknowledgement: {
				type: Boolean,
				default: true
			},
			title: {
				required: true,
				type: String
			},
			dialog: {},
			isAdmin: {
				type: Boolean,
				required: false
			},
			isSaving: {
				type: Boolean,
				required: false
			}
		},
		mixins: [validationMixin, typeFriendlyMixin],

		validations: {
			form: {
				title: { required },
				description: { required },
				startDateUTC: { date: { required } },
				endDateLocal: {},
				reportsTo: {},
				type: { required }
			}
		},

		data() {
			return {
				active_tab: 0,
				form: null,
				positionItemDialog: false,
				positionItemDialogType: null,
				editedPositionItemId: null,
				editedPositionItem: undefined,
				positionTemplateSelectorDialog: false,
				addPositionItemDialog: false,
				positionTypes: [
					{ text: 'Full-time', value: 0 },
					{ text: 'Part-time', value: 1 },
					{ text: 'Casual', value: 2 },
					{ text: 'Fixed Term', value: 3 },
					{ text: 'Shiftworker', value: 4 },
					{ text: 'Daily hire and weekly hire', value: 5 },
					{ text: 'Probation', value: 6 },
					{ text: 'Outworker', value: 7 },
					{ text: 'Consultant', value: 8 },
					{ text: 'Contractor', value: 9 }
				],
				hasReordered: false,
				isLoadingPositionTemplate: false,
				showConfirmDialog: false,
				originalCoreItemsSerialised: null // Serialised version of core items. Core item updates require re-approval
			};
		},

		watch: {
			position: {
				immediate: true,
				deep: true,
				handler() {
					this.form = JSON.parse(JSON.stringify({
						id: this.position.id,
						title: this.position.title,
						description: this.position.description,
						startDateUTC: {
							date: this.$options.filters.formatDate(this.position.startDateUTC, 'YYYY-MM-DD'),
							menu: false
						},
						type: this.position.type,
						reportsTo: this.position.reportsTo,
						positionItems: this.position.positionItems !== null ? this.position.positionItems : []
					}));

					this.originalCoreItemsSerialised = this.serialiseCoreItems();

					this.$v.form.$reset();
				}
			}
		},
		computed: {
			isNew() {
				return this.form.id == null;
			},
			otherEmployees() {
				return this.$props.employees.filter((x) => x.id !== this.employeeId);
			},
			reportsToEmployees() {
				let underlings = [];

				let compileUnderlings = (employeeId) => {
					let employee = this.$store.getters['employees/employeesMini'].find((x) => x.id == employeeId);

					underlings = underlings.concat(employee.underlings);

					employee.underlings.forEach((childEmployeeId) => {
						compileUnderlings(childEmployeeId);
					});
				};

				if (this.employeeId != null) {
					compileUnderlings(this.employeeId);
				}

				let filtered = this.$props.employees.filter((x) => !underlings.includes(x.id) && x.id !== this.employeeId);

				return filtered;
			},
			titleErrors() {
				const errors = [];
				if (!this.$v.form.title.$dirty) return errors;
				!this.$v.form.title.required && errors.push('Title is required.');
				return errors;
			},
			typeErrors() {
				const errors = [];
				if (!this.$v.form.type.$dirty) return errors;
				!this.$v.form.type.required && errors.push('Position Type is required.');
				return errors;
			},
			descriptionErrors() {
				const errors = [];
				if (!this.$v.form.description.$dirty) return errors;
				!this.$v.form.description.required && errors.push('Description is required.');
				return errors;
			},
			startDateUTCErrors() {
				const errors = [];
				if (!this.$v.form.startDateUTC.date.$dirty) return errors;
				!this.$v.form.startDateUTC.date.required && errors.push('Start Date is required.');
				return errors;
			},
			reportsToErrors() {
				const errors = [];
				if (!this.$v.form.reportsTo.$dirty) return errors;
				return errors;
			},
			effectiveDateFormatted() {
				return this.$options.filters.formatDate(this.form.startDateUTC.date);
			},
			kpis() {
				return this.form.positionItems !== null ? this.form.positionItems.filter((x) => x.type == 1) : [];
			},
			keyDuties() {
				return this.form.positionItems !== null ? this.form.positionItems.filter((x) => x.type == 2) : [];
			},
			personSpecification() {
				return this.form.positionItems !== null ? this.form.positionItems.filter((x) => x.type == 5) : [];
			},
			coreItemsSerialised() {
				return this.serialiseCoreItems();
			},
			coreItemsUpdated() {
				return this.coreItemsSerialised !== this.originalCoreItemsSerialised;
			}
		},

		methods: {
			serialiseCoreItems() {
				const coreItems = {
					title: this.form.title,
					positionItems: [...this.form.positionItems]
				}

				return JSON.stringify(coreItems);
			},
			convertToTemplateClick() {
				if (confirm(`Are you sure you wish to create a Template from this ${this.$term('positionDescription', { case: 'title' })}?`)) {
					let positionTemplate = {
						id: this.newGuid(),
						title: this.form.title,
						description: this.form.description,
						department: null,
						positionItems: this.form.positionItems
					};
					this.$emit('convertPositionToTemplate', positionTemplate);
				}
			},
			onSaveClick() {
				this.$v.form.$touch();

				if (this.$v.form.$invalid) {
				} else {
					if (this.position.status > 0) {
						this.showConfirmDialog = true;
					} else {
						this.submit();
					}
				}
			},
			submit(triggerApprovalWorkflow = false) {
				if (this.form.reportsTo != null) {
					this.form.reportsTo.reportsTo = null;
				}

				let postObj = {
					id: this.form.id != undefined ? this.form.id : null,
					title: this.form.title,
					description: this.form.description,
					startDateLocal: this.form.startDateUTC.date,
					endDateUTC: null,
					startDateUTC: this.form.startDateUTC.date,
					endDateLocal: null, // this.form.endDateLocal.date,
					reportsTo: this.form.reportsTo,
					positionItems: this.form.positionItems,
					type: this.form.type,
					triggerApprovalWorkflow
				};

				this.$emit('save', postObj);
			},
			clear() {
				this.$v.form.$reset();
				this.form.title = '';
				this.form.description = '';
				this.form.reportsTo = null;
				this.form.startDateUTC.date = '';
				this.form.positionItems = [];
				this.form.type = 0;
			},
			closeEditor(viaSubmit) {
				if ((this.$v.$anyDirty || this.hasReordered) && !viaSubmit) {
					if (!confirm('You have unsaved changes, do you wish to proceed and lose these unsaved changes?')) {
					} else {
						this.clear();
						this.$emit('closeEditor');
					}
				} else {
					this.clear();
					this.$emit('closeEditor');
				}
			},
			closePositionItemEditor() {
				this.positionItemDialog = false;
				this.addPositionItemDialog = false;
			},
			closePositionTemplateSelector() {
				this.positionTemplateSelectorDialog = false;
			},
			onEditPositionItem(item) {
				this.editedPositionItemId = item.id;
				this.editedPositionItem = JSON.parse(JSON.stringify(item));
				this.positionItemDialogType = this.editedPositionItem.type;
				this.positionItemDialog = true;
			},
			onAddPositionItem(type) {
				this.positionItemDialogType = type;
				this.editedPositionItemId = null;
				this.editedPositionItem = undefined;
				this.positionItemDialog = true;
				// this.addPositionItemDialog = true;
			},
			onSelectPositionTemplate(item) {
				this.closePositionTemplateSelector();

				this.form.title = item.title;
				this.form.description = item.description;

				// Load items
				this.isLoadingPositionTemplate = true;
				this.$store.dispatch('positionTemplates/getById', item.id).then(
					(positionTemplate) => {
						this.isLoadingPositionTemplate = false;
						this.form.positionItems = positionTemplate.positionItems;
					},
					(error) => {
						this.isLoadingPositionTemplate = false;
					}
				);
			},
			onDeletePositionItem(item) {
				const index = this.form.positionItems.findIndex((x) => x.id === item.id);
				confirm('Are you sure you want to delete this duty?') && this.form.positionItems.splice(index, 1) && this.$v.form.$touch();
			},
			onSortOrderUpdated(items) {
				// Creates a new array. Removes sorted chunk and adds them to the end. Probably 50 better ways of doing this.
				const updatedIds = items.map((x) => x.id);

				let newArr = this.form.positionItems.filter((x) => !updatedIds.includes(x.id));

				this.form.positionItems = [...newArr, ...items];

				this.hasReordered = true;
			},
			savePositionItem(positionItem) {
				if (this.editedPositionItemId) {
					const index = this.form.positionItems.findIndex((x) => x.id === this.editedPositionItemId);
					if (index > -1) {
						this.form.positionItems.splice(index, 1, positionItem);
					}
				} else {
					this.form.positionItems.push(positionItem);
				}

				this.$v.form.$touch();
			},
			savePositionItemAddMulti(data) {
				for (let i = 0; i < data.items.length; i++) {
					this.form.positionItems.push({
						id: null,
						category: data.category,
						item: data.items[i].item,
						outcomes: data.items[i].outcomes,
						type: data.type
					});
				}
			}
		}
	};
</script>

<style scoped></style>
